/**
 * The reusable TSL function library (seeded by the Phase 0 spike, per plan).
 *
 * These are plain TypeScript functions that *compose node graphs* at material
 * build time — not `Fn()` shader closures — so material builders can branch on
 * layer definitions in JS and only pay for the features a layer uses.
 */
import { float, mix, select, smoothstep, texture, uv, vec2 } from 'three/tsl';
import { ATLAS_WIDTH } from '../bake.js';
/** nAge = clamp((time − birth) / lifetime, 0, 1). The domain of every over-lifetime curve. */
export function normalizedAge(time, birth, lifetime) {
    return time.sub(birth).div(lifetime.max(1e-6)).clamp(0, 1);
}
/**
 * 1 while the particle is alive, 0 otherwise (multiplied into scale so dead /
 * never-spawned slots collapse to zero-area quads). birth < 0 is the
 * "never spawned" sentinel written by the CPU pool.
 */
export function aliveFactor(time, birth, lifetime) {
    const spawned = birth.greaterThanEqual(0.0);
    const started = time.greaterThanEqual(birth);
    const notDead = time.lessThan(birth.add(lifetime));
    return select(spawned.and(started).and(notDead), float(1), float(0));
}
/**
 * Sample one row of a curve atlas (see bake.ts) with manual linear
 * interpolation between the two nearest texels — identical on WebGPU and
 * WebGL2 regardless of float-filtering support, and bit-comparable to
 * CurveAtlas.sample on the CPU.
 */
export function sampleAtlas(atlas, row, rows, t) {
    const w = ATLAS_WIDTH;
    const x = t.clamp(0, 1).mul(w - 1);
    const x0 = x.floor();
    const f = x.sub(x0);
    const v = float((row + 0.5) / rows);
    const a = texture(atlas, vec2(x0.add(0.5).div(w), v));
    const b = texture(atlas, vec2(x0.add(1.5).div(w), v));
    return mix(a, b, f);
}
/** Scalar curve row (curves are broadcast across RGBA; read red). */
export function sampleCurve(atlas, row, rows, t) {
    return sampleAtlas(atlas, row, rows, t).r;
}
/** Gradient row: full RGBA. */
export function sampleGradient(atlas, row, rows, t) {
    return sampleAtlas(atlas, row, rows, t);
}
/**
 * Procedural soft-disc alpha from quad UVs — no texture needed.
 * hardness 0 = falloff from the center (glow), hardness → 1 = hard-edged disc.
 */
export function radialFalloff(hardness) {
    const d = uv().sub(0.5).length().mul(2);
    const edge0 = Math.min(Math.max(hardness, 0), 0.99);
    return smoothstep(float(edge0), float(1), d).oneMinus();
}
/** Ribbon cross-section falloff (beams/strips): soft edges across uv.y, hardness like radialFalloff. */
export function crossFalloff(hardness) {
    const d = uv().y.mul(2).sub(1).abs();
    const edge0 = Math.min(Math.max(hardness, 0), 0.99);
    return smoothstep(float(edge0), float(1), d).oneMinus();
}
export function billboard(material, nodes) {
    material.positionNode = nodes.center;
    material.scaleNode = nodes.scale;
    material.rotationNode = nodes.rotation;
}
